import { ReactNodeViewRenderer } from "@tiptap/react"
import { Image as TiptapImage } from "@tiptap/extension-image"
import { ImageNodeView } from "@/tiptap/nodes/image-node/image-node-view"
import { NodeSelection, Plugin, PluginKey, TextSelection } from "@tiptap/pm/state"
import { Slice } from "@tiptap/pm/model"
import {
  handleImageSelectionClipboardShortcut,
  handleImageSelectionPaste,
  normalizeFirstPartyImageSrc,
} from "@/tiptap/core/editor-clipboard"

const imageClickSelectPluginKey = new PluginKey("imageClickSelect")

function altTextFromImageSrc(src) {
  if (!src) return null

  const path = src.split(/[?#]/)[0]
  const alt = path.split("/").filter(Boolean).pop()
  return alt || null
}

const parseImageAttributes = img => ({
  src: normalizeFirstPartyImageSrc(img.getAttribute("src")),
  alt: img.getAttribute("alt") || altTextFromImageSrc(img.getAttribute("src")),
  title: img.getAttribute("title"),
  width: img.getAttribute("width"),
  height: img.getAttribute("height"),
  style: img.getAttribute("style") || img.getAttribute("data-mce-style"),
  "data-mce-style": img.getAttribute("data-mce-style"),
})

function isEmojiFallbackImage(img) {
  const src = img.getAttribute("src") || ""
  const alt = img.getAttribute("alt") || ""

  return Boolean(
    img.closest('[data-type="emoji"]') ||
    (src.includes("emoji-datasource-") && alt.endsWith(" emoji"))
  )
}

const FIRST_PARTY_IMAGE_PATH_PREFIXES = [
  "/public_photo/",
  "/photo/",
  "/file/",
  "/uploads/",
]

function isBareFirstPartyImagePath(text) {
  if (typeof text !== "string") return false

  const trimmed = text.trim()
  if (!trimmed || /\s/.test(trimmed)) return false

  return FIRST_PARTY_IMAGE_PATH_PREFIXES.some((prefix) => trimmed.startsWith(prefix))
}

function sliceIsOnlyImages(slice, imageType) {
  if (!slice?.content?.childCount) return false

  let onlyImages = true
  slice.content.forEach((child) => {
    if (child.type !== imageType) onlyImages = false
  })

  return onlyImages
}

function sliceContainsImage(slice, imageType) {
  let found = false
  slice?.content?.descendants?.((node) => {
    if (node.type === imageType) found = true
    return !found
  })

  return found
}

function buildImageHTMLAttributes(attrs) {
  const result = { src: attrs.src || "" }

  if (attrs.alt) result.alt = attrs.alt
  if (attrs.title) result.title = attrs.title
  if (attrs.width) result.width = attrs.width
  if (attrs.height) result.height = attrs.height
  if (attrs.style) result.style = attrs.style
  if (attrs["data-mce-style"]) result["data-mce-style"] = attrs["data-mce-style"]

  return result
}

export const Image = TiptapImage.extend({
  content: "inline*",

  addAttributes() {
    return {
      ...this.parent?.(),
      "data-align": {
        default: null,
      },
      style: {
        default: null,
        keepOnSplit: false,
      },
      "data-mce-style": {
        default: null,
        keepOnSplit: false,
      },
      showCaption: {
        default: false,
        parseHTML: (element) => {
          return (element.tagName === "FIGURE" || element.getAttribute("data-show-caption") === "true");
        },
        renderHTML: (attributes) => {
          if (!attributes.showCaption) return {}
          return { "data-show-caption": "true" }
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (node) => {
          const img = node.querySelector("img")
          if (!img) return false
          if (isEmojiFallbackImage(img)) return false

          return {
            ...parseImageAttributes(img),
            "data-align": node.getAttribute("data-align"),
            showCaption: true,
          };
        },
        contentElement: "figcaption",
      },
      {
        tag: "img[src]",
        getAttrs: (node) => {
          if (node.closest("figure")) return false
          if (isEmojiFallbackImage(node)) return false

          return {
            ...parseImageAttributes(node),
            "data-align": node.getAttribute("data-align"),
            showCaption: false,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, title, width, height, style, showCaption } = node.attrs
    const align = node.attrs["data-align"]

    const imgAttrs = buildImageHTMLAttributes({
      src,
      alt,
      title,
      width,
      height,
      style,
      "data-mce-style": node.attrs["data-mce-style"],
    })

    const hasContent = node.content.size > 0

    if (showCaption || hasContent) {
      const figureAttrs = {
        "data-url": src || "",
      }
      if (showCaption) figureAttrs["data-show-caption"] = "true"
      if (align) figureAttrs["data-align"] = align

      return ["figure", figureAttrs, ["img", imgAttrs], ["figcaption", {}, 0]]
    }

    if (align) imgAttrs["data-align"] = align
    return ["img", imgAttrs]
  },

  // Image nodes use content: "inline*" for captions. On mobile, taps often land a
  // TextSelection caret inside that empty content (top-left of the image) instead
  // of a NodeSelection — especially after the node was selected once (selectednode
  // becomes read-write for clipboard). Promote those carets back to NodeSelection,
  // and handle clicks the same way on desktop and mobile.
  addProseMirrorPlugins() {
    const imageType = this.type

    return [
      new Plugin({
        key: imageClickSelectPluginKey,
        // Paste over a selected image must be intercepted in the capture
        // phase: the read-write CSS workaround for crbug.com/1233054 makes
        // the selected node view isContentEditable, so TipTap's stopEvent
        // swallows the paste event before ProseMirror sees it and Chromium
        // pastes natively into the node view DOM (raw text ends up in the
        // image caption). See handleImageSelectionPaste.
        view(editorView) {
          const onPasteCapture = (event) =>
            handleImageSelectionPaste(editorView, event)

          editorView.dom.addEventListener("paste", onPasteCapture, true)
          return {
            destroy() {
              editorView.dom.removeEventListener("paste", onPasteCapture, true)
            },
          }
        },
        appendTransaction: (_transactions, _oldState, newState) => {
          const { selection } = newState
          if (!(selection instanceof TextSelection) || !selection.empty) {
            return null
          }

          const { $from } = selection
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth)
            if (node.type !== imageType) continue

            // Allow a real caret in the caption when caption editing is active.
            if (node.attrs.showCaption || node.content.size > 0) {
              return null
            }

            return newState.tr.setSelection(
              NodeSelection.create(newState.doc, $from.before(depth))
            )
          }

          return null
        },
        props: {
          handleClickOn: (view, _pos, node, nodePos, event, direct) => {
            if (!direct || node.type !== imageType || !view.editable) return false

            const target = event.target
            if (!(target instanceof Element)) return false
            if (target.closest(".tiptap-image-caption")) return false
            if (target.closest(".tiptap-image-handle")) return false
            if (!target.closest(".tiptap-image-img, .tiptap-image-content")) {
              return false
            }

            view.dispatch(
              view.state.tr.setSelection(
                NodeSelection.create(view.state.doc, nodePos)
              )
            )
            return true
          },
          handlePaste: (view, event, slice) => {
            if (!view.editable) return false

            // Chromium's async-clipboard sanitizer strips data-pm-slice, so a
            // copied image arrives as an open slice. Pasting an open image
            // slice over a selection unwraps the image to its (empty) inline
            // content — deleting the selection and inserting nothing. Close
            // image-only slices so the image itself is inserted.
            const hasProseMirrorSliceMetadata =
            event?.clipboardData?.getData?.("text/html")?.includes("data-pm-slice")
            
            if (
              sliceIsOnlyImages(slice, imageType) &&
              !hasProseMirrorSliceMetadata
            ) {
              if (slice.openStart === 0 && slice.openEnd === 0) return false

              view.dispatch(
                view.state.tr
                  .replaceSelection(new Slice(slice.content, 0, 0))
                  .scrollIntoView(),
              )
              return true
            }

            // Clipboard sanitizers can mangle the copied <img> HTML entirely,
            // leaving only the text/plain path. Rebuild the image node instead
            // of inserting a raw /public_photo/... string.
            const text = event?.clipboardData?.getData?.("text/plain") || ""
            const insideCodeBlock = view.state.selection.$from.parent.type.spec.code

            if (
              !insideCodeBlock &&
              !sliceContainsImage(slice, imageType) &&
              isBareFirstPartyImagePath(text)
            ) {
              const src = normalizeFirstPartyImageSrc(text.trim())
              const node = imageType.create({
                src,
                alt: altTextFromImageSrc(src),
              })

              view.dispatch(
                view.state.tr.replaceSelectionWith(node).scrollIntoView(),
              )
              return true
            }

            return false
          },
        },
      }),
    ]
  },

  addKeyboardShortcuts() {
    return {
      // Chromium skips copy/cut clipboard events for selected
      // contenteditable=false node views (https://crbug.com/1233054), so we
      // handle the shortcuts on keydown with the async Clipboard API instead.
      "Mod-c": ({ editor }) =>
        handleImageSelectionClipboardShortcut(editor.view),
      "Mod-x": ({ editor }) =>
        handleImageSelectionClipboardShortcut(editor.view, { cut: true }),
      "Mod-a": ({ editor }) => {
        const { state, view } = editor
        const { selection } = state
        const { $from } = selection

        let imagePos = null
        let imageNode = null

        for (let depth = $from.depth; depth >= 0; depth--) {
          const nodeAtDepth = $from.node(depth)
          if (nodeAtDepth.type === this.type) {
            imageNode = nodeAtDepth
            // posBefore is the resolved position *before* this node
            imagePos = depth === 0 ? 0 : $from.before(depth)
            break
          }
        }

        // Not inside an Image → let default behavior happen
        if (!imageNode || imagePos == null) {
          return false
        }

        // If the caption/content is empty, allow the default progressive select-all
        const contentIsEmpty =
          imageNode.content.size === 0 || imageNode.textContent.length === 0

        if (contentIsEmpty) {
          return false
        }

        // Compute the content range of the image node:
        // content starts at (nodePos + 1) and ends at (nodePos + node.nodeSize - 1)
        const start = imagePos + 1
        const end = imagePos + imageNode.nodeSize - 1

        const tr = state.tr.setSelection(TextSelection.create(state.doc, start, end))
        view.dispatch(tr)

        return true
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
})

export default Image
